import { fromJS } from "immutable";
import _ from "lodash";
import AppConstants from "@/constants/AppConstants";
import {
  DEFAULT_BUTTON_VALUES,
  BUTTON_PROPERTY_META_MAP,
  BUTTON_PROPERTY_ORDER,
  BUTTON_HOVER_PROPERTY_ORDER,
} from "./ButtonPropertyMaps";
import { elementSyncUtils } from "../SyncUtils";
import { isDeviation } from "../InheritanceUtils";
import { IS_DEVIATION } from "../../constants/DesignConstants.mjs";
import { MERGE, SPLIT } from "@/constants/PropUpdateConstants.mjs";
import { getProp, inheritProp, shouldPropInherit } from "../Merge/MergeUtils.mjs";
import { LAYOUT_DESKTOP, LAYOUTS } from "../../constants/DesignConstants.mjs";

const { LOCK_EDITING_KEY } = AppConstants;

const prepareEventHandlers = (inputEventHandlers, onSIInput, onSIChange, updaterArguments) =>
  inputEventHandlers.reduce((result, callbackName) => {
    // todo: figure out a better way to connect these.
    const handler = callbackName === "onSIInput" ? onSIInput : onSIChange;
    result[callbackName] = handler(...updaterArguments);
    return result;
  }, {});

// todo: type side effects

/**
 * @typedef {import("immutable").Map} ImmutableMap
 * @typedef {import("@/vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 *
 * @typedef {Object} MappedElementLayout
 * @property {string} name - The name of the property.
 * @property {*} value - The value of the property.
 * @property {boolean} isInherited - Indicates if the property is inherited.
 * @property {boolean} isInheritedFromTheme - Indicates if the property is inherited from the theme.
 * @property {boolean} isDefault - Indicates if the property is set to its default value.
 * @property {boolean} isSynced - Indicates if the property is synced.
 * @property {boolean} reserveContext - Reserved context flag, always true.
 * @property {boolean} context - Context of the property, should be false if the property is a sub-property.
 * @property {{[key: string]: MappedElementLayout}} subProps - Sub-properties of the property.
 * @property {Function} toggleSync - Function to toggle the synced state of the property.
 * @property {Object} controlMeta - Additional control metadata.
 * @property {Object} preparedHandlers - Prepared event handlers
 */

/**
 * Prepares a callback for the style changes.
 * @param {*} onSIChange
 * @param {ImmutableMap} buttonElem
 * @returns {(e: CustomEvent) => void}
 */
const onSetButtonStyle = (onSIChange, buttonElem) =>
  onSIChange("style", buttonElem.get("style"), null, buttonElem);

/**
 * Maps a property for a button element, determining its value and handlers.
 *
 * @param {Object} props - The properties for mapping.
 * @param {ImmutableMap} props.buttonElem - The button element map.
 * @param {boolean=} props.canMerge - Prop supplied by control meta to hide split merge ui.
 * @param {string} props.prop - The property name to map.
 * @param {string} props.layout - The layout context (e.g., mobile, desktop).
 * @param {Function} props.isSynced - Function to check if the property is synced.
 * @param {ImmutableMap} props.theme - The theme map for styling.
 * @param {Function} props.onSIInput - Handler for input events.
 * @param {Function} props.onSIChange - Handler for change events.
 * @param {Function=} props.removeCustomization - Remove customization function. Passed through if supplied.
 * @param {Function} props.toggleSyncedProp - Function to toggle the synced state of a property.
 * @param {Object=} props.metaMapOverride - BUTTON_PROPERTY_META_MAP override.
 * @returns {MappedElementLayout} - Mapped property details including value and event handlers.
 */
const mapProp = ({
  buttonElem,
  canMerge,
  prop,
  layout,
  isSynced,
  theme,
  onSIInput,
  onSIChange,
  removeCustomization,
  toggleSyncedProp,
  metaMapOverride,
}) => {
  const name = prop;
  const propIsSynced = isSynced(prop);

  const elementValue = getProp(buttonElem, layout, prop);

  // todo: I get it now, but maybe rename these to be a little clearer?
  // todo: for naming, isInherited is also used to render props in design settings
  const isInherited = elementValue === undefined;
  const isInheritedFromTheme = shouldPropInherit(buttonElem, layout, prop, theme);
  const isDefault = isInherited && !isInheritedFromTheme;

  const value = inheritProp(buttonElem, layout, prop, theme) ?? DEFAULT_BUTTON_VALUES[prop];

  // todo: type the control meta coming out
  const controlMeta = (metaMapOverride ?? BUTTON_PROPERTY_META_MAP)[prop] || {};
  const {
    updateCallback,
    inputEventHandlers = [],
    children = [],
    context = true,
    sideEffects = {},
  } = controlMeta;

  const processedSideEffects = Object.entries(sideEffects).reduce((result, [propName, process]) => {
    result[propName] = process(buttonElem, layout);
    return result;
  }, {});

  // todo: get this from the params of the return type of the function that makes onSIChange
  const updaterArguments = [
    name,
    value,
    propIsSynced ? LAYOUT_DESKTOP : layout,
    buttonElem,
    updateCallback,
  ];

  const preparedHandlers = prepareEventHandlers(
    inputEventHandlers,
    onSIInput,
    onSIChange,
    updaterArguments,
  );

  const subProps = children.reduce((result, childPropName) => {
    result[childPropName] = mapProp({
      buttonElem,
      canMerge: false,
      prop: childPropName,
      layout,
      isSynced: () => propIsSynced,
      theme,
      onSIInput,
      onSIChange,
      toggleSyncedProp,
    });

    return result;
  }, {});

  return {
    canMerge,
    name,
    value,
    isInherited,
    isInheritedFromTheme,
    isDefault,
    isSynced: propIsSynced,
    reserveContext: true,
    context,
    removeCustomization,
    subProps,
    toggleSync: () => toggleSyncedProp(name, value, layout),
    isLocked: buttonElem.get(LOCK_EDITING_KEY) === "1",
    ...controlMeta,
    ...preparedHandlers,
    ...processedSideEffects,
  };
};

/**
 * Maps a hover property for a button element, determining its value and handlers.
 *
 * @param {Object} props - The properties for mapping.
 * @param {ImmutableMap} props.buttonElem - The button element map.
 * @param {string} props.prop - The property name to map.
 * @param {ImmutableMap} props.theme - The theme map for styling.
 * @param {Function} props.onSIInput - Handler for input events.
 * @param {Function} props.onSIChange - Handler for change events.
 * @param {Function=} props.removeCustomization - Remove customization function. Passed through if supplied.
 * @param {Object=} props.metaMapOverride - BUTTON_PROPERTY_META_MAP override.
 * @returns {MappedElementLayout} - Mapped property details including value and event handlers.
 */
const mapHoverProp = ({
  buttonElem,
  prop,
  theme,
  onSIInput,
  onSIChange,
  removeCustomization,
  metaMapOverride,
}) => {
  const name = prop;
  const layout = LAYOUT_DESKTOP;

  const basePropName = prop.split(".").slice(1).join(".");

  const elementValue = getProp(buttonElem, layout, prop);

  const isInherited = elementValue === undefined;
  const isInheritedFromTheme = shouldPropInherit(buttonElem, layout, prop, theme);
  const isDefault = isInherited && !isInheritedFromTheme;

  // TODO: Commenting because it broke Design Settings inputs
  // TODO: Update to work with buttonElem from both prop panel and design settings
  // const buttonStyle = buttonElem.get("style");
  // const buttonStyles = theme.getIn(["styles", buttonStyle]);
  // const hoverProp = `hover.${basePropName}`;
  // const themeHoverValue = buttonStyles ? getProp(buttonStyles, layout, hoverProp) : undefined;
  // const inheritedBaseValue = buttonStyles
  //   ? inheritProp(buttonStyles, layout, basePropName, theme)
  //   : undefined;

  const value =
    getProp(buttonElem, layout, prop) ??
    // Inherit from theme's hover value first, then fall back to normal value
    inheritProp(buttonElem, layout, prop, theme) ??
    inheritProp(buttonElem, layout, basePropName, theme) ??
    DEFAULT_BUTTON_VALUES[prop] ??
    DEFAULT_BUTTON_VALUES[basePropName];

  const controlMeta = (metaMapOverride ?? BUTTON_PROPERTY_META_MAP)[prop] || {};
  const { updateCallback, inputEventHandlers = [], children = [], context = true } = controlMeta;

  const updaterArguments = [name, value, LAYOUT_DESKTOP, buttonElem, updateCallback];

  const preparedHandlers = prepareEventHandlers(
    inputEventHandlers,
    onSIInput,
    onSIChange,
    updaterArguments,
  );

  const subProps = children.reduce((result, childPropName) => {
    const baseSubPropName = childPropName.split(".").slice(1).join(".");

    result[baseSubPropName] = mapHoverProp({
      buttonElem,
      prop: childPropName,
      theme,
      onSIInput,
      onSIChange,
      removeCustomization,
    });

    return result;
  }, {});

  return {
    name,
    value,
    isInherited,
    isInheritedFromTheme,
    isDefault,
    isSynced: true,
    reserveContext: true,
    context,
    removeCustomization,
    subProps,
    isLocked: buttonElem.get(LOCK_EDITING_KEY) === "1",
    ...controlMeta,
    ...preparedHandlers,
  };
};

/**
 * Maps button properties to their respective handlers and configurations.
 *
 * @param {ImmutableMap} buttonElem - The button element data.
 * @param {Function} onSIChange - Handler for style input change events.
 * @param {Function} onSIInput - Handler for style input events.
 * @param {ImmutableMap} theme - The theme configuration.
 * @param {string} layout - The layout context (e.g., desktop, mobile).
 * @param {Function} onToggleSync - Function to toggle the sync state of a property.
 * @param {Function} onCustomizationRemoval - Function to toggle the sync state of a property.
 * @param {Object=} metaMapOverride - BUTTON_PROPERTY_META_MAP override.
 * @returns {ImmutableMap} - A map of the button properties and their configurations.
 */
export const ButtonPropertyMapper = (
  buttonElem,
  onSIChange,
  onSIInput,
  theme,
  layout,
  onToggleSync,
  onCustomizationRemoval,
  metaMapOverride,
) => {
  let mappedElem = fromJS({});

  // Set the top level properties
  mappedElem = mappedElem
    .set("data", buttonElem)
    .set("type", buttonElem.get("type"))
    .set("style", buttonElem.get("style"))
    .set("onSetStyle", onSetButtonStyle(onSIChange, buttonElem))
    .set("label", buttonElem.get("label"))
    .set("id", buttonElem.get("id"))
    .set("isLocked", buttonElem.get(LOCK_EDITING_KEY) === "1")
    .set(IS_DEVIATION, isDeviation(buttonElem));

  const { isSynced } = elementSyncUtils(
    buttonElem,
    onSIChange,
    layout,
    undefined,
    undefined,
    theme,
  );

  const toggleSyncedProp = (propName, value, layout) => {
    const propIsSynced = isSynced(propName);
    const propUpdate = { name: propName, intent: propIsSynced ? SPLIT : MERGE, value, layout };

    onToggleSync(propUpdate);
  };

  // Map properties for mobile and desktop
  LAYOUTS.forEach((layout) => {
    const propData = BUTTON_PROPERTY_ORDER.map((prop) =>
      mapProp({
        buttonElem,
        prop,
        layout,
        isSynced,
        theme,
        onSIInput,
        onSIChange,
        removeCustomization: () => onCustomizationRemoval(prop),
        toggleSyncedProp,
        metaMapOverride,
      }),
    );

    const hoverProps = BUTTON_HOVER_PROPERTY_ORDER.map((prop) =>
      mapHoverProp({
        buttonElem,
        prop,
        theme,
        onSIInput,
        onSIChange,
        removeCustomization: () => onCustomizationRemoval(prop),
        metaMapOverride,
      }),
    );

    mappedElem = mappedElem.set(layout, [...propData, ...hoverProps]);
  });

  return mappedElem;
};

export default ButtonPropertyMapper;
